/**
 * useUsers — Hook for user management CRUD operations
 */

import { useState, useCallback, useEffect } from "react";
import useApi from "./useApi";
import { endpoints } from "config/api";
import logger from "services/logger";

export default function useUsers({ autoFetch = false } = {}) {
  const { get, put, post, del } = useApi();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await get(endpoints.users.base);
      setUsers(data.users || []);
      return data.users;
    } catch (err) {
      logger.error("[useUsers]", "Failed to fetch users:", err);
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [get]);

  const getUser = useCallback(
    async (uid) => {
      try {
        const data = await get(endpoints.users.byId(uid));
        return data.user;
      } catch (err) {
        logger.error("[useUsers]", `Failed to fetch user ${uid}:`, err);
        throw err;
      }
    },
    [get]
  );

  const updateUser = useCallback(
    async (uid, userData) => {
      try {
        const data = await put(endpoints.users.byId(uid), userData);
        setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, ...data.user } : u)));
        return data.user;
      } catch (err) {
        logger.error("[useUsers]", `Failed to update user ${uid}:`, err);
        throw err;
      }
    },
    [put]
  );

  const suspendUser = useCallback(
    async (uid) => {
      try {
        await post(endpoints.users.suspend(uid));
        setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, suspended: true } : u)));
      } catch (err) {
        logger.error("[useUsers]", `Failed to suspend user ${uid}:`, err);
        throw err;
      }
    },
    [post]
  );

  const unsuspendUser = useCallback(
    async (uid) => {
      try {
        await post(endpoints.users.unsuspend(uid));
        setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, suspended: false } : u)));
      } catch (err) {
        logger.error("[useUsers]", `Failed to unsuspend user ${uid}:`, err);
        throw err;
      }
    },
    [post]
  );

  useEffect(() => {
    if (autoFetch) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch]);

  return {
    users,
    loading,
    error,
    fetchUsers,
    getUser,
    updateUser,
    suspendUser,
    unsuspendUser,
  };
}
